import {
  buildWahaPayload, formFromView, isFormDirty, validateWahaForm, wahaErrorKey,
  type WahaCheckResult, type WahaErrorKey, type WahaSettingsForm, type WahaSettingsPayload, type WahaSettingsView,
  type WahaTestResult
} from "./wahaSettingsModel";

// State and actions behind Admin → Settings → WhatsApp, kept free of React so the button wiring
// is unit-testable in the node test environment. Rules:
//  - load only reads; Save only saves; Check only checks; nothing ever triggers a test send
//    except the explicit "Send test message" action.
//  - Check / Send test use the SAVED configuration: blocked while the form has unsaved changes
//    or the saved configuration is incomplete. Both work while delivery is disabled.
//  - Send test targets the saved test recipient only (request body `{}`); blocked when none is saved.
//  - One action at a time; the typed API key is cleared after every save attempt.

export interface WahaUiState {
  view: WahaSettingsView | null;
  form: WahaSettingsForm | null;
  loadError: WahaErrorKey | null;
  error: WahaErrorKey | null;
  saved: boolean;
  busy: "save" | "check" | "test" | null;
  checkResult: WahaCheckResult | null;
  checkError: WahaErrorKey | null;
  testResult: WahaTestResult | null;
  testError: WahaErrorKey | null;
}

export const initialWahaUi: WahaUiState = {
  view: null, form: null, loadError: null, error: null, saved: false, busy: null,
  checkResult: null, checkError: null, testResult: null, testError: null
};

export interface WahaApi {
  get: () => Promise<WahaSettingsView>;
  save: (payload: WahaSettingsPayload) => Promise<WahaSettingsView>;
  check: () => Promise<{ result: WahaCheckResult }>;
  sendTest: () => Promise<{ result: WahaTestResult }>;
}

export type WahaActionBlock = "incomplete" | "unsaved" | "noRecipient" | null;

/** Why "Check connection" is unavailable (null = available). */
export function checkBlock(ui: WahaUiState): WahaActionBlock {
  if (!ui.view?.complete) return "incomplete";
  if (ui.form && isFormDirty(ui.form, ui.view)) return "unsaved";
  return null;
}

/** Why "Send test message" is unavailable (null = available). Independent of the enabled switch. */
export function testBlock(ui: WahaUiState): WahaActionBlock {
  return checkBlock(ui) ?? (ui.view?.testRecipient ? null : "noRecipient");
}

export interface WahaActions {
  load: () => Promise<void>;
  update: (patch: Partial<WahaSettingsForm>) => void;
  save: () => Promise<void>;
  check: () => Promise<void>;
  sendTest: () => Promise<void>;
}

type Patch = (s: WahaUiState) => Partial<WahaUiState>;

export function createWahaActions(api: WahaApi, get: () => WahaUiState, set: (patch: Patch) => void): WahaActions {
  let inFlight = false;
  const withoutKey = (s: WahaUiState) => ({ form: s.form ? { ...s.form, apiKey: "" } : s.form });

  const run = async (kind: "save" | "check" | "test", work: () => Promise<void>) => {
    if (inFlight) return;
    inFlight = true;
    set(() => ({ busy: kind }));
    try {
      await work();
    } finally {
      inFlight = false;
      set(() => ({ busy: null }));
    }
  };

  return {
    load: async () => {
      try {
        const view = await api.get();
        set(() => ({ view, form: formFromView(view), loadError: null }));
      } catch (e) {
        set(() => ({ loadError: wahaErrorKey(e) }));
      }
    },

    update: (patch) => set((s) => ({ saved: false, form: s.form ? { ...s.form, ...patch } : s.form })),

    save: async () => {
      const { form, view } = get();
      if (!form || !view || inFlight) return;
      const invalid = validateWahaForm(form, view.hasApiKey);
      if (invalid) {
        set((s) => ({ error: invalid, saved: false, ...withoutKey(s) }));
        return;
      }
      await run("save", async () => {
        set(() => ({ error: null, saved: false }));
        try {
          const next = await api.save(buildWahaPayload(form));
          // Fresh saved state (incl. the saved test recipient shown beside the test button);
          // results of earlier check/test runs described the previous settings, so drop them.
          set(() => ({
            view: next, form: formFromView(next), saved: true,
            checkResult: null, checkError: null, testResult: null, testError: null
          }));
        } catch (e) {
          set((s) => ({ error: wahaErrorKey(e), ...withoutKey(s) }));
        }
      });
    },

    check: async () => {
      if (checkBlock(get())) return;
      await run("check", async () => {
        set(() => ({ checkResult: null, checkError: null }));
        try {
          const { result } = await api.check();
          set(() => ({ checkResult: result }));
        } catch (e) {
          set(() => ({ checkError: wahaErrorKey(e) }));
        }
      });
    },

    sendTest: async () => {
      if (testBlock(get())) return;
      await run("test", async () => {
        set(() => ({ testResult: null, testError: null }));
        try {
          const { result } = await api.sendTest();
          set(() => ({ testResult: result }));
        } catch (e) {
          set(() => ({ testError: wahaErrorKey(e) }));
        }
      });
    }
  };
}
